import React, { useCallback, useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { clientAPI } from '../services/api';
import { formatUSPhoneInput, toTwilioUSPhone, formatPhoneDisplay, looksLikePhone } from '../utils/phone';
import { Skeleton } from './Skeleton';
import './CustomerManager.css';

export default function CustomerManager() {
  const [clients, setClients]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [clientForm, setClientForm]   = useState({ name: '', phone: '' });
  const [searchQuery, setSearchQuery] = useState('');

  // Details modal
  const [detailsClient, setDetailsClient] = useState(null);
  const [visits, setVisits]               = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);

  // Edit state
  const [editingClient, setEditingClient] = useState(null);
  const [editFields, setEditFields]       = useState({ name: '', phone: '' });
  const [editSaving, setEditSaving]       = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clientAPI.getAll();
      setClients(response.data || []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleDelete = async (client) => {
    if (!window.confirm(`Delete customer "${client.name}"? This cannot be undone.`)) return;
    try {
      await clientAPI.delete(client._id);
      setClients((prev) => prev.filter((c) => c._id !== client._id));
    } catch {
      toast.error('Failed to delete customer.');
    }
  };

  const handleDetails = async (client) => {
    setDetailsClient(client);
    setVisits([]);
    setVisitsLoading(true);
    try {
      const res = await clientAPI.getVisits(client.phone);
      setVisits(res.data || []);
    } catch {
      setVisits([]);
    } finally {
      setVisitsLoading(false);
    }
  };

  const closeModal = () => { setDetailsClient(null); setEditingClient(null); };

  const startEdit = (client) => {
    setEditingClient(client._id);
    setEditFields({ name: client.name || '', phone: client.phone || '' });
  };

  const cancelEdit = () => setEditingClient(null);

  const saveEdit = async (client) => {
    setEditSaving(true);
    try {
      const updated = await clientAPI.update(client._id, {
        name: editFields.name,
        phone: toTwilioUSPhone(editFields.phone),
      });
      setClients((prev) => prev.map((c) => c._id === client._id ? { ...c, ...updated.data } : c));
      if (detailsClient && detailsClient._id === client._id) {
        setDetailsClient({ ...detailsClient, ...updated.data });
      }
      setEditingClient(null);
    } catch {
      toast.error('Failed to save changes.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleSearchChange = (e) => {
    const raw = e.target.value;
    if (looksLikePhone(raw) && raw.trim()) {
      setSearchQuery(formatUSPhoneInput(raw));
    } else {
      setSearchQuery(raw);
    }
  };

  const searchResults = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) return [];
    if (looksLikePhone(q)) {
      const qDigits = q.replace(/\D/g, '');
      return clients.filter((c) => (c.phone || '').replace(/\D/g, '').includes(qDigits));
    }
    const qLower = q.toLowerCase();
    return clients.filter((c) => (c.name || '').toLowerCase().includes(qLower));
  }, [clients, searchQuery]);

  return (
    <div className="customer-manager">
      {/* Add Customer */}
      <div className="customer-section">
        <h3>Add Customer</h3>
        <form
          className="customer-form"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              const normalizedPhone = toTwilioUSPhone(clientForm.phone);
              if (normalizedPhone) {
                const existing = await clientAPI.findByPhone(normalizedPhone);
                if (existing.data) {
                  const existingName = existing.data.name || existing.data.clientName || 'Unknown';
                  const proceed = window.confirm(
                    `Phone number ${clientForm.phone} is already registered to "${existingName}".\n\nAdd anyway?`
                  );
                  if (!proceed) return;
                }
              }
              await clientAPI.create({ name: clientForm.name, phone: normalizedPhone });
              setClientForm({ name: '', phone: '' });
              fetchClients();
              toast.success('Customer added');
            } catch {
              toast.error('Failed to add customer');
            }
          }}
        >
          <input
            placeholder="Name"
            value={clientForm.name}
            onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
            required
          />
          <input
            placeholder="Phone Number"
            value={clientForm.phone}
            onChange={(e) => setClientForm({ ...clientForm, phone: formatUSPhoneInput(e.target.value) })}
          />
          <button type="submit">Save Customer</button>
        </form>
      </div>

      {/* Customer Search */}
      <div className="customer-section">
        <h3>Customer Search</h3>
        <input
          className="customer-search-input"
          placeholder="Search by name or phone number..."
          value={searchQuery}
          onChange={handleSearchChange}
        />
        {loading ? (
          <Skeleton.Table rows={3} />
        ) : searchQuery.trim() === '' ? (
          <p className="customer-muted">Type a name or phone number to search.</p>
        ) : searchResults.length === 0 ? (
          <p className="customer-muted">No customers found.</p>
        ) : (
          <table className="customer-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone Number</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {searchResults.map((client) => (
                <tr key={client._id}>
                  <td>{client.name || 'N/A'}</td>
                  <td>{formatPhoneDisplay(client.phone)}</td>
                  <td className="customer-actions">
                    <button className="btn-details" onClick={() => handleDetails(client)}>Details</button>
                    <button className="btn-edit" onClick={() => { handleDetails(client); startEdit(client); }}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(client)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Details Modal */}
      {detailsClient && (
        <div className="cm-modal-overlay" onClick={closeModal}>
          <div className="cm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cm-modal-header">
              <div>
                {editingClient === detailsClient._id ? (
                  <div className="cm-edit-form">
                    <label className="cm-edit-label">
                      Name
                      <input
                        value={editFields.name}
                        onChange={(e) => setEditFields((f) => ({ ...f, name: e.target.value }))}
                        placeholder="Full name"
                      />
                    </label>
                    <label className="cm-edit-label">
                      Phone
                      <input
                        value={editFields.phone}
                        onChange={(e) => setEditFields((f) => ({ ...f, phone: formatUSPhoneInput(e.target.value) }))}
                        placeholder="+1-000-000-0000"
                      />
                    </label>
                    <div className="cm-edit-actions">
                      <button className="cm-btn-save" onClick={() => saveEdit(detailsClient)} disabled={editSaving}>
                        {editSaving ? '⏳ Saving…' : '✓ Save'}
                      </button>
                      <button className="cm-btn-cancel" onClick={cancelEdit} disabled={editSaving}>✕ Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="cm-modal-name">{detailsClient.name}</div>
                    <div className="cm-modal-phone">{formatPhoneDisplay(detailsClient.phone)}</div>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                {editingClient !== detailsClient._id && (
                  <button className="cm-btn-edit" onClick={() => startEdit(detailsClient)}>✏ Edit</button>
                )}
                <button className="cm-modal-close" onClick={closeModal}>X</button>
              </div>
            </div>
            <h4>Visit History</h4>
            {visitsLoading ? (
              <Skeleton.Table rows={2} />
            ) : visits.length === 0 ? (
              <p className="customer-muted">No appointment history found.</p>
            ) : (
              <table className="customer-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Service</th>
                    <th>Technician</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.map((v) => (
                    <tr key={v._id}>
                      <td>{v.appointmentDate ? new Date(v.appointmentDate).toLocaleDateString() : '-'}</td>
                      <td>{v.startTime || '-'}</td>
                      <td>{v.serviceName || v.serviceId || '-'}</td>
                      <td>{v.techName || v.nailTechId || '-'}</td>
                      <td>
                        <span className={`cm-status cm-status-${v.status || 'unknown'}`}>
                          {v.status || '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
